import React, { useState, useLayoutEffect, useRef, createContext, useContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
const LoadingContext = createContext({
  isLoading: true,
  setIsLoading: () => {},
  isFading: false,
  readyPath: '',
  markRouteReady: () => {}
});
export const useLoading = () => useContext(LoadingContext);
const DEVELOPMENT_MODE = false;
const FREEZE_OVERLAY = false;
const DISABLE_LOADING_OVERLAY = false;
const FIRST_LOAD_PROGRESS_DELAY_MS = 500;
const FIRST_LOAD_DISPLAY_MS = 1800;
const FIRST_LOAD_FINISH_PAUSE_MS = 200;
const FIRST_LOAD_FADE_MS = 1000;
const NAVIGATION_PROGRESS_MS = 600;
const NAVIGATION_FADE_MS = 400;
const FIRST_LOAD_MAX_WAIT_MS = 2800;
const NAVIGATION_MAX_WAIT_MS = 1800;

const waitForImage = (image) => new Promise((resolve) => {
  if (!image) {
    resolve();
    return;
  }

  const finish = () => {
    image.removeEventListener('load', finish);
    image.removeEventListener('error', finish);

    if (image.complete && image.naturalWidth > 0 && typeof image.decode === 'function') {
      image.decode().catch(() => {}).finally(resolve);
      return;
    }

    resolve();
  };

  if (image.complete) {
    finish();
    return;
  }

  image.addEventListener('load', finish, { once: true });
  image.addEventListener('error', finish, { once: true });
});

const waitForCriticalVisuals = () => {
  const activeHeroImage = document.querySelector(
    '.home-hero-background img.is-active, .sell-hero-picture img, .home-hero-background img'
  );
  const fontReady = document.fonts?.ready?.catch(() => {}) || Promise.resolve();

  return Promise.all([fontReady, waitForImage(activeHeroImage)]);
};

const LoadingOverlay = () => {
  const [progress, setProgress] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [progressOpacity, setProgressOpacity] = useState(1);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const { isLoading, setIsLoading, setIsFading, readyPath } = useLoading();
  const location = useLocation();
  const overlayRef = useRef(null);
  const cycleRef = useRef(null);
  const readyPathRef = useRef(readyPath);

  readyPathRef.current = readyPath;
  
  useLayoutEffect(() => {
    if (DISABLE_LOADING_OVERLAY) {
      setIsLoading(false);
      setIsFading(false);
      return undefined;
    }

    const hasLoaded = localStorage.getItem('hasLoadedBefore');
    const shouldShowIntro = !hasLoaded || DEVELOPMENT_MODE;
    setIsFirstLoad(shouldShowIntro);

    setProgress(0);
    setOpacity(1);
    setProgressOpacity(1);
    setIsLoading(true);
    setIsFading(false);

    if (FREEZE_OVERLAY) {
      return undefined;
    }

    const minimumDisplayMs = shouldShowIntro ? FIRST_LOAD_DISPLAY_MS : NAVIGATION_PROGRESS_MS;
    const maximumWaitMs = shouldShowIntro ? FIRST_LOAD_MAX_WAIT_MS : NAVIGATION_MAX_WAIT_MS;
    const fadeDurationMs = shouldShowIntro ? FIRST_LOAD_FADE_MS : NAVIGATION_FADE_MS;
    const cycle = {
      path: location.pathname,
      shouldShowIntro,
      routeReady: readyPathRef.current === location.pathname,
      criticalVisualsReady: false,
      minimumDisplayComplete: false,
      finishing: false,
      criticalCheckStarted: false,
      progressStartTimer: null,
      finishDelayTimer: null,
      hideTimer: null,
      progressInterval: null
    };
    cycleRef.current = cycle;

    const finish = (force = false) => {
      if (cycleRef.current !== cycle || cycle.finishing) return;
      if (!force && (!cycle.routeReady || !cycle.criticalVisualsReady || !cycle.minimumDisplayComplete)) return;

      cycle.finishing = true;
      if (cycle.progressInterval) {
        clearInterval(cycle.progressInterval);
        cycle.progressInterval = null;
      }
      setProgress(100);
      setProgressOpacity(0);

      const fadeOverlay = () => {
        if (cycleRef.current !== cycle) return;
        setIsFading(true);
        setOpacity(0);
        cycle.hideTimer = setTimeout(() => {
          if (cycleRef.current !== cycle) return;
          setIsLoading(false);
          if (cycle.shouldShowIntro && !DEVELOPMENT_MODE) {
            localStorage.setItem('hasLoadedBefore', 'true');
          }
        }, fadeDurationMs);
      };

      if (cycle.shouldShowIntro) {
        cycle.finishDelayTimer = setTimeout(fadeOverlay, FIRST_LOAD_FINISH_PAUSE_MS);
      } else {
        fadeOverlay();
      }
    };

    const startCriticalCheck = () => {
      if (cycle.criticalCheckStarted || !cycle.routeReady) return;
      cycle.criticalCheckStarted = true;
      waitForCriticalVisuals().then(() => {
        if (cycleRef.current !== cycle) return;
        cycle.criticalVisualsReady = true;
        finish();
      });
    };

    cycle.finish = finish;
    cycle.startCriticalCheck = startCriticalCheck;

    if (shouldShowIntro) {
      cycle.progressStartTimer = setTimeout(() => {
        if (cycleRef.current !== cycle || cycle.finishing) return;
        cycle.progressInterval = setInterval(() => {
          setProgress((currentProgress) => Math.min(90, currentProgress + 0.7));
        }, 10);
      }, FIRST_LOAD_PROGRESS_DELAY_MS);
    } else {
      const navigationStartTime = performance.now();
      cycle.progressInterval = setInterval(() => {
        const navigationProgress = Math.min(
          92,
          ((performance.now() - navigationStartTime) / NAVIGATION_PROGRESS_MS) * 92
        );
        setProgress(navigationProgress);
      }, 16);
    }

    const minimumDisplayTimer = setTimeout(() => {
      cycle.minimumDisplayComplete = true;
      finish();
    }, minimumDisplayMs);

    const maximumWaitTimer = setTimeout(() => {
      finish(true);
    }, maximumWaitMs);

    startCriticalCheck();

    return () => {
      clearTimeout(minimumDisplayTimer);
      clearTimeout(maximumWaitTimer);
      if (cycle.progressStartTimer) clearTimeout(cycle.progressStartTimer);
      if (cycle.progressInterval) clearInterval(cycle.progressInterval);
      if (cycle.finishDelayTimer) clearTimeout(cycle.finishDelayTimer);
      if (cycle.hideTimer) clearTimeout(cycle.hideTimer);
      if (cycleRef.current === cycle) cycleRef.current = null;
    };
  }, [location.pathname, setIsLoading, setIsFading]);

  useLayoutEffect(() => {
    const cycle = cycleRef.current;
    if (!cycle || cycle.path !== location.pathname || readyPath !== location.pathname) return;

    cycle.routeReady = true;
    cycle.startCriticalCheck?.();
    cycle.finish?.();
  }, [location.pathname, readyPath]);

  useLayoutEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay || DISABLE_LOADING_OVERLAY || (!isLoading && !FREEZE_OVERLAY)) {
      return undefined;
    }

    const preventOverlayScroll = (event) => {
      if (event.cancelable) {
        event.preventDefault();
      }
    };

    overlay.addEventListener('wheel', preventOverlayScroll, { passive: false });
    overlay.addEventListener('touchmove', preventOverlayScroll, { passive: false });

    return () => {
      overlay.removeEventListener('wheel', preventOverlayScroll);
      overlay.removeEventListener('touchmove', preventOverlayScroll);
    };
  }, [isLoading]);

  if (DISABLE_LOADING_OVERLAY || (!isLoading && !FREEZE_OVERLAY)) return null;

  const progressBarStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '6px',
    width: `${progress}%`,
    backgroundColor: isFirstLoad ? '#fff' : '#333333',
    transition: 'width 0.2s linear, opacity 0.4s ease-out',
    opacity: progressOpacity,
    pointerEvents: 'none',
  };

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isFirstLoad ? '#001120' : '#fff',
    zIndex: 2147483647,
    opacity: opacity,
    transition: isFirstLoad
      ? `opacity ${FIRST_LOAD_FADE_MS / 1000}s ease-out`
      : `opacity ${NAVIGATION_FADE_MS / 1000}s cubic-bezier(0.22, 1, 0.36, 1)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'all',
    transform: 'translateZ(999999px)',
    willChange: 'opacity',
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          @keyframes revealRect {
  0% {
    clip-path: polygon(0 0, 0 0, 0 100%, 0 100%);
    opacity: 1;
  }
  100% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    opacity: 1;
  }
}
          
          .logo-path {
            animation: fadeIn 0.8s ease-out forwards;
            opacity: 0;
            transform-box: fill-box;
            transform-origin: center;
          }
          
          .logo-container {
            opacity: 1;
            transition: opacity 0.4s ease-out;
          }
          
          .logo-path:nth-child(1) { animation-delay: 0s; }
          .logo-path:nth-child(2) { animation-delay: 0.1s; }
          .logo-path:nth-child(3) { animation-delay: 0.2s; }
          .logo-path:nth-child(4) { animation-delay: 0.3s; }
          .logo-path:nth-child(5) { animation-delay: 0.4s; }
          .logo-path:nth-child(6) { animation-delay: 0.5s; }
          .logo-path:nth-child(7) { animation-delay: 0.6s; }
          .logo-path:nth-child(8) { animation-delay: 0.7s; }
          .logo-path:nth-child(9) { animation-delay: 0.8s; }
          .logo-path:nth-child(10) { animation-delay: 0.9s; }
          .logo-path:nth-child(11) { animation-delay: 1s; }
          .mobile-rect {
  animation: revealRect 0.8s cubic-bezier(0.77, 0, 0.175, 1) forwards;
  opacity: 0;
  clip-path: polygon(0 0, 0 0, 0 100%, 0 100%);
}
          .mobile-rect:nth-child(1) {
  animation-delay: 0s;
}
.mobile-rect:nth-child(2) {
  animation-delay: 0s;
}
.mobile-rect:nth-child(3) {
  animation-delay: 0.4s;
}
.mobile-rect:nth-child(4) {
  animation-delay: 0.4s;
}
          @media (max-width: 1000px) {
            .desktop-logo {
              display: block;
            }
            
            .mobile-logo {
              display: none;
            }
          }
          @media (min-width: 1001px) {
            .desktop-logo {
              display: block;
            }
            
            .mobile-logo {
              display: none;
            }
          }
        `}
      </style>
      <div
        ref={overlayRef}
        style={overlayStyle}
        className="overlayLogo"
      >
        <div style={progressBarStyle} />
        {isFirstLoad && (
          <>
            {/* Desktop Version */}
            <svg
              className="desktop-logo logo-container"
              width="567"
              height="90"
              viewBox="0 0 567 90"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                width: '300px',
                height: 'auto',
                opacity: progressOpacity,
                transition: 'opacity 0.4s ease-out'
              }}
            >
              <path className="logo-path" d="M563.105 72H552.1V64.261H563.105V72Z" fill="white"/>
              <path className="logo-path" d="M547.267 50.061C547.267 57.232 544.214 63.054 538.108 67.527C533.209 71.148 526.322 73.278 518.583 73.278C510.844 73.278 503.886 71.077 498.987 67.527C492.881 63.054 489.828 57.232 489.828 50.061C489.828 43.032 492.881 37.21 499.058 32.737C504.028 29.116 511.057 26.844 518.583 26.844C526.109 26.844 533.067 29.116 538.037 32.737C544.143 37.21 547.267 43.032 547.267 50.061ZM538.392 50.061C538.392 41.186 530.369 34.583 518.583 34.583C506.726 34.583 498.632 41.257 498.632 50.061C498.632 59.22 506.442 65.61 518.583 65.61C530.653 65.61 538.392 59.22 538.392 50.061Z" fill="white"/>
              <path className="logo-path" d="M482.711 38.843L474.901 41.541C470.499 37.068 464.677 34.583 457.08 34.583C444.584 34.583 436.703 40.973 436.703 49.99C436.703 59.22 444.442 65.539 457.222 65.539C464.677 65.539 470.286 63.338 475.043 58.368L482.356 62.273C475.54 69.941 467.801 73.136 457.009 73.136C439.401 73.136 427.828 63.409 427.828 49.99C427.828 42.961 430.881 37.21 436.987 32.737C441.886 29.187 448.915 26.915 456.512 26.915C467.446 26.915 475.966 30.749 482.711 38.843Z" fill="white"/>
              <path className="logo-path" d="M419.551 72.213C412.877 71.858 406.7 70.154 400.878 67.101C395.269 70.935 388.24 72.923 380.643 72.923C368.076 72.923 359.911 66.107 359.911 57.516C359.911 50.132 365.946 44.878 377.803 42.038C376.028 39.198 375.176 36.5 375.176 33.518C375.176 30.678 376.17 27.909 377.945 25.637C380.856 21.874 385.471 19.673 390.441 19.673C398.748 19.673 404.357 24.998 404.357 32.595C404.357 39.056 399.742 44.097 391.009 46.653C394.133 50.629 397.541 54.179 401.304 57.374C404.357 54.96 406.7 50.913 408.688 45.162H417.421C415.504 51.481 412.167 56.948 407.481 61.776C411.315 63.48 415.362 64.332 419.551 64.332V72.213ZM386.181 40.476C392.713 39.695 396.05 36.926 396.05 32.382C396.05 29.4 393.565 26.915 390.157 26.915C386.252 26.915 383.554 29.613 383.554 33.518C383.554 35.506 384.477 37.849 386.181 40.476ZM394.843 61.989C390.37 57.942 386.323 53.469 382.631 48.57C373.188 50.061 368.502 53.185 368.502 57.8C368.502 62.273 373.33 65.539 381.495 65.539C385.542 65.539 389.944 64.332 394.843 61.989Z" fill="white"/>
              <path className="logo-path" d="M355.155 72H344.434L327.039 54.889L309.644 72H298.923L321.643 49.848L299.704 28.335H310.425L327.039 44.807L343.653 28.335H354.374L332.435 49.848L355.155 72Z" fill="white"/>
              <path className="logo-path" d="M288.723 57.658C288.723 66.675 279.067 73.278 263.376 73.278C247.614 73.278 237.958 66.675 237.958 57.658V28.335H246.478V55.954C246.478 62.202 252.229 65.61 263.376 65.61C274.452 65.61 280.203 62.131 280.203 55.954V28.335H288.723V57.658Z" fill="white"/>
              <path className="logo-path" d="M232.8 72H184.591V20.596H193.111V64.261H232.8V72Z" fill="white"/>
              <svg
                className="logo-path"
                width="151"
                height="90"
                viewBox="0 0 151 90"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="11.7462" y="33.5054" width="62.2583" height="16.7585" transform="rotate(44.5 11.7462 33.5054)" fill="white"/>
                <rect x="96.6268" y="77.4817" width="60.6529" height="16.7585" transform="rotate(-46 96.6268 77.4817)" fill="white"/>
                <rect x="43.8268" y="0.851562" width="108.238" height="16.7585" transform="rotate(45 43.8268 0.851562)" fill="white"/>
                <rect x="33.2631" y="76.5356" width="108.238" height="16.7585" transform="rotate(-45 33.2631 76.5356)" fill="white"/>
              </svg>
            </svg>
          </>
        )}
      </div>
    </>
  );
};
export const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(!DISABLE_LOADING_OVERLAY);
  const [isFading, setIsFading] = useState(false);
  const [readyPath, setReadyPath] = useState('');
  const markRouteReady = useCallback((path) => {
    setReadyPath(path);
  }, []);
  
  return (
    <LoadingContext.Provider value={{ isLoading, setIsLoading, isFading, setIsFading, readyPath, markRouteReady }}>
      <LoadingOverlay />
      {children}
    </LoadingContext.Provider>
  );
};

export const RouteReady = ({ children }) => {
  const { pathname } = useLocation();
  const { markRouteReady } = useLoading();

  useLayoutEffect(() => {
    markRouteReady(pathname);
  }, [markRouteReady, pathname]);

  return children;
};

export default LoadingProvider;
